/**
 * Notification inbox - bell icon with dropdown panel.
 * Shows recent admin notifications with read/unread state.
 */

import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../../lib/admin-api.js'
import type { AdminNotification } from '@provendoro/shared/types'

const typeIcons: Record<AdminNotification['type'], string> = {
  new_order: 'O',
  low_stock: 'S',
  quote_request: 'Q',
  order_ready: 'R',
  payment_received: '$',
}

const typeColors: Record<AdminNotification['type'], string> = {
  new_order: 'var(--color-primary)',
  low_stock: 'var(--color-warning)',
  quote_request: 'oklch(0.65 0.15 70)',
  order_ready: 'var(--color-success)',
  payment_received: 'oklch(0.55 0.15 140)',
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

interface NotificationInboxProps {
  /** When true, renders the notification list inline (no bell icon, no dropdown) */
  inlineMode?: boolean
}

export function NotificationInbox({ inlineMode = false }: NotificationInboxProps) {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 30_000,
  })

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['notifications'] }) },
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['notifications'] }) },
  })

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const unreadCount = data?.unreadCount ?? 0
  const notifications = data?.notifications ?? []

  // Inline mode: render the notification list directly without bell/dropdown
  if (inlineMode) {
    return (
      <div>
        {unreadCount > 0 && (
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              className="text-xs font-medium"
              style={{ color: 'var(--color-primary)' }}
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
            >
              Mark all read
            </button>
          </div>
        )}
        {notifications.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No notifications</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                type="button"
                className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[var(--color-surface)]"
                style={{ backgroundColor: !notif.readAt ? 'oklch(0.97 0.01 260)' : 'transparent' }}
                onClick={() => {
                  if (!notif.readAt) markReadMutation.mutate(notif.id)
                }}
              >
                <span
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: typeColors[notif.type] ?? 'var(--color-primary)' }}
                  aria-hidden="true"
                >
                  {typeIcons[notif.type]}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight" style={{ color: 'var(--color-text)' }}>{notif.title}</p>
                  <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>{notif.body}</p>
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{timeAgo(notif.createdAt)}</p>
                </div>
                {!notif.readAt && (
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: 'var(--color-primary)' }} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors"
        style={{ backgroundColor: open ? 'var(--color-surface)' : 'transparent', color: 'var(--color-text-secondary)' }}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {/* Bell icon */}
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-bold text-white"
            style={{ backgroundColor: 'var(--color-error)', fontSize: '10px' }}
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl shadow-2xl"
          style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-xs font-medium"
                style={{ color: 'var(--color-primary)' }}
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No notifications</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  type="button"
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors"
                  style={{ backgroundColor: !notif.readAt ? 'oklch(0.97 0.01 260)' : 'transparent' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = !notif.readAt ? 'oklch(0.97 0.01 260)' : 'transparent' }}
                  onClick={() => {
                    if (!notif.readAt) markReadMutation.mutate(notif.id)
                  }}
                >
                  <span
                    className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: typeColors[notif.type] ?? 'var(--color-primary)' }}
                    aria-hidden="true"
                  >
                    {typeIcons[notif.type]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight" style={{ color: 'var(--color-text)' }}>{notif.title}</p>
                    <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>{notif.body}</p>
                    <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{timeAgo(notif.createdAt)}</p>
                  </div>
                  {!notif.readAt && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: 'var(--color-primary)' }} aria-hidden="true" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
